/**
 * FileOutputCards — clickable cards for agent-generated file outputs.
 *
 * Extracted from ChatPanel.tsx (legacy) for use by AgUiChatPanel. ChatPanel.tsx
 * keeps its own local copy — this file is AG-UI-only. If the two panels ever
 * converge, promote this out of /agui and drop the legacy copy.
 */

import { Icon } from '@amzn/awsui-components-react';

export interface FileOutputInfo {
  path: string;       // e.g. "output/test/test_20260501.md"
  filename: string;   // e.g. "test_20260501.md"
  s3Key?: string;     // e.g. "webbwu/rocky/output/test/test_20260501.md"
  contentType?: string;
  size?: number;
}

/**
 * Read a `file_send` tool result — the Runtime's JSON
 * `{"file_output": true, "path", "filename", "s3_key", "size", "content_type"}`.
 * `null` for anything else: an `{"error": ...}` result, or the `{"restored":true}`
 * stand-in that history replay uses because no tool results are persisted.
 */
export function parseFileSendResult(result: string | undefined): FileOutputInfo | null {
  if (!result) return null;
  let data: unknown;
  try {
    data = JSON.parse(result);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.file_output !== true || typeof d.path !== 'string' || typeof d.filename !== 'string') {
    return null;
  }
  return {
    path: d.path,
    filename: d.filename,
    s3Key: typeof d.s3_key === 'string' ? d.s3_key : undefined,
    contentType: typeof d.content_type === 'string' ? d.content_type : undefined,
    size: typeof d.size === 'number' ? d.size : undefined,
  };
}

interface MessageLike {
  id: string;
  role: string;
  toolCallId?: string;
  content?: unknown;
  toolCalls?: Array<{ id: string; function?: { name?: string } }>;
}

/** Did a `file_send` after message `sinceId` return a file? */
export function fileSentSince(messages: readonly MessageLike[], sinceId: string): boolean {
  const start = messages.findIndex((m) => m.id === sinceId);
  if (start < 0) return false;
  const fileSendCalls = new Set<string>();
  for (const m of messages.slice(start + 1)) {
    if (m.role === 'assistant') {
      for (const tc of m.toolCalls ?? []) {
        if (tc.function?.name === 'file_send') fileSendCalls.add(tc.id);
      }
    } else if (
      m.role === 'tool'
      && typeof m.toolCallId === 'string'
      && fileSendCalls.has(m.toolCallId)
      && typeof m.content === 'string'
      && parseFileSendResult(m.content)
    ) {
      return true;
    }
  }
  return false;
}

interface FileOutputCardsProps {
  files: FileOutputInfo[];
  onFileClick: (path: string) => void;
}

export default function FileOutputCards({ files, onFileClick }: FileOutputCardsProps) {
  if (!files.length) return null;
  return (
    <div className="file-output-cards">
      {files.map((f, i) => (
        <button
          key={i}
          className="file-output-card"
          onClick={() => onFileClick(f.path)}
          title={f.path}
        >
          <Icon name="file" size="normal" />
          <span className="file-output-card__name">{f.filename}</span>
          <Icon name="angle-right" size="small" />
        </button>
      ))}
    </div>
  );
}